import { RouteNames } from '@/routes'
import { storeActions } from '@/store/actions'
import { useStoreDispatch } from '@/store/hooks'
import { userPasswordSchema } from '@/validation-schemas'
import { Button } from '@repo/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@repo/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/form'
import { useFormValidator, useT } from '@repo/ui/hooks'
import { Input } from '@repo/ui/input'
import { Label } from '@repo/ui/label'
import { Circle, Info, ShieldCheck } from 'lucide-react'
import { useMemo } from 'react'
import { Trans } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

export function ResetPasswordRoute() {
  const { token, email } = useParams<{ token: string; email: string }>()
  const decodedEmail = email ? decodeURIComponent(email) : ''
  const dispatch = useStoreDispatch()
  const navigate = useNavigate()

  const { t } = useT({ keyPrefix: 'resetPassword' })

  const defaultValues = useMemo(
    () => ({ password: '', confirmPassword: '' }),
    [],
  )

  const { form, isFormValid, submit, submitOnPressMetaEnter } =
    useFormValidator({
      defaultValues,
      onSubmit: async ({ password, confirmPassword }) => {
        if (password !== confirmPassword) {
          form.setError('confirmPassword', {
            message: t('passwordMatchError'),
          })
          return
        }

        if (!token) {
          // Input validation handled in UI directly instead of toast
          form.setError('password', {
            message: t('invalidToken'),
          })
          return
        }

        if (!decodedEmail) {
          // Input validation handled in UI directly instead of toast
          form.setError('password', {
            message: t('missingEmail'),
          })
          return
        }

        await dispatch(
          storeActions.user.resetPassword({
            token,
            password,
            email: decodedEmail,
          }),
        )
        navigate('/' + RouteNames.Login)
      },
      schema: userPasswordSchema,
    })

  return (
    <div className="relative inset-0 z-10 m-auto flex h-full flex-1 flex-col items-center justify-center gap-2">
      <div className="bg-primary-50 border-primary-200 text-primary-600 mb-6 flex w-96 flex-col items-center justify-center gap-2 rounded-lg border p-4 text-center font-semibold">
        <div className="flex gap-2">
          <Info className="h-6 w-6" /> {t('info')}
        </div>
        <a href="https://caseai.org" className="hover:text-primary underline">
          {t('infoCta')}
        </a>
      </div>
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>{t('title')}</CardTitle>
          <CardDescription>
            <Trans
              i18nKey="resetPassword.description"
              components={[<Label className="text-primary">CaseAI</Label>]}
            />
          </CardDescription>
        </CardHeader>
        <Form {...form}>
          <form onSubmit={submit} onKeyDown={submitOnPressMetaEnter}>
            <CardContent className="grid gap-2">
              {decodedEmail && (
                <div className="text-muted-foreground mb-2 text-sm">
                  {t('resetForEmail')}: <strong>{decodedEmail}</strong>
                </div>
              )}
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('newPassword')}</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="••••••••••••••"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('confirmPassword')}</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="••••••••••••••"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
            <CardFooter>
              <Button className="w-full" type="submit" disabled={!isFormValid}>
                <div className="mr-2">
                  {isFormValid ? (
                    <ShieldCheck className="size-5" />
                  ) : (
                    <Circle className="size-5 opacity-50" />
                  )}
                </div>
                {t('resetPassword')}
              </Button>
            </CardFooter>
          </form>
        </Form>
      </Card>
    </div>
  )
}
